import {Util} from '../util/Util';

/**
 * A single HTTP call, fully described.
 *
 * Every Project operation is RPC-style: the verb and the URL path carry all the
 * arguments and there is no request body to inspect. Two defects that shipped
 * were purely a mismatch here -- `deleteProject` issued as POST against a route
 * registered as DELETE, and Project names sent raw to routes that base64-decode
 * them -- and neither is observable without a deployed server, because the
 * resulting 404 is swallowed by the service's error handler and the UI carries
 * on as if the call had worked.
 *
 * Keeping the wire format in this module, free of any Angular import, means it
 * can be asserted by a plain `ts-node` smoke test
 * (tests/ProjectUrlShape.smoke.ts) with no browser, no server and no DI
 * container. `ProjectService` is then a thin dispatcher over these.
 *
 * The expectations encoded here are transcribed from
 * SnowfallPressServerless/src/handlers/express/ProjectRoutes.ts. When a route
 * changes there, it changes here, and the smoke test is what notices.
 */
export interface EndpointCall {
  verb: 'GET' | 'POST' | 'PUT' | 'DELETE';
  url: string;
}

export class ProjectEndpoints {
  static readonly ENDPOINT = 'project';

  static base(apiBaseURL: string): string {
    return `${apiBaseURL}${ProjectEndpoints.ENDPOINT}`;
  }

  // --- create ---------------------------------------------------------------

  /**
   * Route: POST /createnewproject/:projectName([A-Za-z0-9+/=]+)/:folder(*)
   * Server decodes projectName ONCE and folder TWICE.
   */
  static createNewProject(apiBaseURL: string, projectName: string, folder: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/createnewproject/${Util.toBase64(projectName)}/${Util.toBase64(Util.toBase64(folder))}`
    };
  }

  /** Route: POST /createproject  (consolidated; arguments travel in the body). */
  static createProject(apiBaseURL: string): EndpointCall {
    return { verb: 'POST', url: `${ProjectEndpoints.base(apiBaseURL)}/createproject` };
  }

  /**
   * Route: POST /createnewprojectwithdraft/:draftId([0-9]+)/:projectName([A-Za-z0-9+/=]+)
   * projectName MUST be base64 -- sending it raw meant any name containing a
   * space failed to match the route at all.
   */
  static createNewProjectWithDraft(apiBaseURL: string, draftId: number, projectName: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/createnewprojectwithdraft/${draftId}/${Util.toBase64(projectName)}`
    };
  }

  /** Route: POST /createnewprojectwithbook/:bookId([0-9]+)/:projectName([A-Za-z0-9+/=]+) */
  static createNewProjectWithBook(apiBaseURL: string, bookId: number, projectName: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/createnewprojectwithbook/${bookId}/${Util.toBase64(projectName)}`
    };
  }

  // --- delete ---------------------------------------------------------------

  /**
   * Route: DELETE /deleteproject/:projectId([0-9]+)/:onlyDeleteIfEmptyProject([0-1])
   *
   * DELETE, not POST. There is no body.
   */
  static deleteProject(apiBaseURL: string, projectId: number, onlyDeleteIfEmptyProject: boolean): EndpointCall {
    return {
      verb: 'DELETE',
      url: `${ProjectEndpoints.base(apiBaseURL)}/deleteproject/${projectId}/${onlyDeleteIfEmptyProject ? 1 : 0}`
    };
  }

  // --- move -----------------------------------------------------------------

  /** Route: POST /movebooktoproject/:bookId/:currentProjectId/:newProjectId */
  static moveBookToProject(apiBaseURL: string, bookId: number, currentProjectId: number, newProjectId: number): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/movebooktoproject/${bookId}/${currentProjectId}/${newProjectId}`
    };
  }

  /** Route: POST /movedrafttoproject/:draftId/:currentProjectId/:newProjectId */
  static moveDraftToProject(apiBaseURL: string, draftId: number, currentProjectId: number, newProjectId: number): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/movedrafttoproject/${draftId}/${currentProjectId}/${newProjectId}`
    };
  }

  // --- convert --------------------------------------------------------------

  /** Route: POST /makedraftintobook/:draftId([0-9]+)/:makePrimary([0-1]) */
  static makeDraftIntoBook(apiBaseURL: string, draftId: number, makePrimary: boolean): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/makedraftintobook/${draftId}/${makePrimary ? 1 : 0}`
    };
  }

  /** Route: POST /makebookintodraft/:bookId([0-9]+)/:makePrimary([0-1]) */
  static makeBookIntoDraft(apiBaseURL: string, bookId: number, makePrimary: boolean): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/makebookintodraft/${bookId}/${makePrimary ? 1 : 0}`
    };
  }

  // --- primary --------------------------------------------------------------

  /** Route: POST /setbookasprimary/:projectId/:bookId */
  static setBookAsPrimary(apiBaseURL: string, projectId: number, bookId: number): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/setbookasprimary/${projectId}/${bookId}`
    };
  }

  /** Route: POST /setdraftasprimary/:projectId/:draftId */
  static setDraftAsPrimary(apiBaseURL: string, projectId: number, draftId: number): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/setdraftasprimary/${projectId}/${draftId}`
    };
  }

  // --- merge ----------------------------------------------------------------

  /** Route: POST /mergeprojects/:primary/:obsoleting/:primaryBook/:primaryDraft/:projectName([A-Za-z0-9+/=]+) */
  static mergeProjects(apiBaseURL: string, primaryProjectId: number, obsoletingProjectId: number, primaryBookId: number, primaryDraftId: number, projectName: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/mergeprojects/${primaryProjectId}/${obsoletingProjectId}/${primaryBookId}/${primaryDraftId}/${Util.toBase64(projectName)}`
    };
  }

  // --- folders --------------------------------------------------------------

  /**
   * Route: PUT /project/:projectId([0-9]+)/folder
   *
   * The one endpoint that can express the ROOT. Everything else in this module
   * is RPC-in-the-URL, and that is exactly why the root was unreachable: the
   * root folder is the empty string, `Util.toBase64('')` is `''`, and an empty
   * path segment matches no route. Here the value travels in the BODY, as a
   * plain string, NOT base64:
   *
   *     { "folder": "/Fiction", "currentFolder": "" }
   *
   * `currentFolder` is optional optimistic concurrency, compared RAW by the
   * server with no normalization — so `''` stays `''` and must not be turned
   * into `'/'` on the way out. Omitting it makes the write unconditional.
   *
   * A refusal comes back as `{status:'ERROR', message, data:{folder:'<stored>'}}`
   * with the actual stored value, so a stale dialog can re-render rather than
   * re-fetch. A no-op move returns SUCCESS.
   *
   * After the `folder` table migration this same route takes
   * `{ folder_ID: number|null }`; the string form stays until `project.folder`
   * is retired. Shipped server-side 2026-09-02.
   */
  static setProjectFolder(apiBaseURL: string, projectId: number): EndpointCall {
    return { verb: 'PUT', url: `${ProjectEndpoints.base(apiBaseURL)}/${projectId}/folder` };
  }

  /**
   * Route: POST /movefolders/:projectId/:currrentFolder(*)/:newFolder(*)
   * Server decodes each path ONCE.
   *
   * The pattern was `([A-Za-z0-9+/=]+)` until 2026-09-02 and is now `(*)`, so
   * empty segments match and decode to `''`. Superseded for single-Project
   * moves by {@link setProjectFolder} — kept because `moveProjectsInFolder`
   * still lives in the same family and because nothing is gained by deleting a
   * working route.
   */
  static moveFolders(apiBaseURL: string, projectId: number, currentFolder: string, newFolder: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/movefolders/${projectId}/${Util.toBase64(currentFolder)}/${Util.toBase64(newFolder)}`
    };
  }

  /**
   * Route: POST /moveprojectsinfolder/:currrentFolder(*)/:newFolder(*)
   * Server decodes each path TWICE.
   */
  static moveProjectsInFolder(apiBaseURL: string, currentFolder: string, newFolder: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/moveprojectsinfolder/${Util.toBase64(Util.toBase64(currentFolder))}/${Util.toBase64(Util.toBase64(newFolder))}`
    };
  }

  // --- misc -----------------------------------------------------------------

  /** Route: POST /formProjects/:orgId/:args */
  static formProjects(apiBaseURL: string, orgId: number, args: string): EndpointCall {
    return {
      verb: 'POST',
      url: `${ProjectEndpoints.base(apiBaseURL)}/formProjects/${orgId}/${args}`
    };
  }
}

